import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { nbaToday, shortDate } from "@/lib/format";
import { useSlate } from "@/hooks/useSlate";
import { GamesPanel } from "@/components/triple/GamesPanel";
import { PlayersPanel } from "@/components/triple/PlayersPanel";
import { TeamProjectionsPanel } from "@/components/teamproj/TeamProjections";
import { GameCenter } from "@/components/triple/GameCenter";
import { EuroGameCenter } from "@/components/triple/EuroGameCenter";
import "@/components/triple/TripleView.css";

const shift = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

export default function TripleView({ center = "players" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [day, setDay] = useState(params.get("day") || nbaToday());
  const [gameId, setGameId] = useState(params.get("game"));
  const [league, setLeague] = useState("nba");
  useEffect(() => {
    if (params.get("day")) setDay(params.get("day"));
    if (params.get("game")) setGameId(params.get("game"));
  }, [params]);
  const { data, error, livePlayers } = useSlate(day, league);
  const nba = league === "nba";
  const games = useMemo(() => data?.games || [], [data]);
  useEffect(() => {
    if (data?.day !== day) return;
    if (games.length && !games.some((g) => g.id === gameId)) setGameId((games.find((g) => g.status === "in") || games[0]).id);
  }, [games, gameId, data, day]);
  const rows = useMemo(() => (data?.rows || []).map((r) => (livePlayers[r.game_id] ? { ...r, live: livePlayers[r.game_id][r.player_id] || {} } : r)), [data, livePlayers]);
  const game = games.find((g) => g.id === gameId);
  // A clicked player+stat ("market") in the center table opens a RotoBot-style check card in the right panel.
  const [check, setCheck] = useState(null);
  useEffect(() => { setCheck(null); }, [gameId, day, league]);

  // Day-wide value feed (player + team), shared by the game badges, the game card and the day summary — one source so
  // the badge count can never disagree with what's shown. Refreshes every 60s and immediately after an admin scan.
  const [playerPicks, setPlayerPicks] = useState([]);
  const [teamPicks, setTeamPicks] = useState([]);
  const loadPlayer = useCallback(() => api.get("/value/picks", { params: { day } }).then((r) => setPlayerPicks(r.data.picks || [])).catch(() => {}), [day]);
  const loadTeam = useCallback(() => api.get("/team-value", { params: { day } }).then((r) => setTeamPicks(r.data.picks || [])).catch(() => {}), [day]);
  useEffect(() => {
    setPlayerPicks([]); setTeamPicks([]);
    if (!nba) return undefined;  // Euro has no odds/value feed yet
    loadPlayer(); loadTeam();
    const t = setInterval(() => { loadPlayer(); loadTeam(); }, 60000);
    return () => clearInterval(t);
  }, [loadPlayer, loadTeam, nba]);
  const onValueChange = useCallback((picks) => { if (Array.isArray(picks)) setPlayerPicks(picks); loadPlayer(); loadTeam(); }, [loadPlayer, loadTeam]);

  // Euro market odds (assos365): fetched per day, keyed by game_id, drives the game-card market view + value badges.
  const [euroOdds, setEuroOdds] = useState({});
  const [euroValueCounts, setEuroValueCounts] = useState({});
  const [euroLiveCounts, setEuroLiveCounts] = useState({});
  useEffect(() => {
    setEuroOdds({});
    setEuroValueCounts({});
    setEuroLiveCounts({});
    if (nba) return undefined;
    let alive = true;
    const loadEuro = () => {
      api.get("/euro/odds", { params: { comp: league, day } })
        .then((r) => { if (alive) setEuroOdds(Object.fromEntries((r.data.games || []).map((g) => [g.game_id, g]))); })
        .catch(() => {});
      api.get("/value/counts", { params: { comp: league, day } })
        .then((r) => { if (alive) setEuroValueCounts(r.data || {}); })
        .catch(() => {});
      api.get("/value/live-euro", { params: { comp: league } })  // auto-pulls Assos365 live lines + counts live value markets
        .then((r) => { if (alive) setEuroLiveCounts(r.data?.counts || {}); })
        .catch(() => {});
    };
    loadEuro();
    const t = setInterval(loadEuro, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [nba, league, day]);

  const valueCounts = useMemo(() => {
    const c = {};
    if (nba) {
      playerPicks.forEach((p) => { if (p.is_value !== false) c[p.game_id] = (c[p.game_id] || 0) + 1; });
    } else {
      Object.values(euroOdds).forEach((g) => {
        const n = ["total", "spread"].filter((k) => g.edge?.[k]?.value).length;
        if (n) c[g.game_id] = n;
      });
      Object.entries(euroValueCounts).forEach(([gid, n]) => { c[gid] = (c[gid] || 0) + n; });  // player prop value picks
      Object.entries(euroLiveCounts).forEach(([gid, n]) => { c[gid] = (c[gid] || 0) + n; });  // live value markets now
    }
    return c;
  }, [playerPicks, euroOdds, euroValueCounts, euroLiveCounts, nba]);
  const openPlayer = useCallback((id) => id && navigate(`/player/${id}`), [navigate]);
  const openTeamGame = useCallback((id) => { setGameId(id); if (center !== "team") navigate(`/team-projections?day=${day}&game=${id}`); }, [center, day, navigate]);
  const switchLeague = useCallback((l) => {
    setLeague(l); setGameId(null);
    if (l === "nba") { setDay(nbaToday()); return; }
    api.get("/euro/calendar", { params: { comp: l } }).then((r) => {
      const days = r.data.days || [];
      const today = nbaToday();
      const pick = [...days].reverse().find((d) => d <= today) || days[days.length - 1];
      if (pick) setDay(pick);
    }).catch(() => {});
  }, []);

  return (
    <div className="tv-root" data-testid="triple-view">
      <div className="tv-daybar">
        <button type="button" onClick={() => setDay(shift(day, -1))} aria-label="Previous day" data-testid="tv-prev-day"><ChevronLeft size={16} /></button>
        <span data-testid="tv-day">{day === nbaToday() ? "Today · " : ""}{shortDate(day)}</span>
        <button type="button" onClick={() => setDay(shift(day, 1))} aria-label="Next day" data-testid="tv-next-day"><ChevronRight size={16} /></button>
        {error && <span className="tv-error">{error}</span>}
      </div>
      <div className="tv-grid">
        <GamesPanel games={games} selectedId={gameId} onSelect={setGameId} valueCounts={valueCounts}
          league={league} onLeague={switchLeague}
          playerPicks={playerPicks} teamPicks={teamPicks} onOpenPlayer={openPlayer} onOpenTeamGame={openTeamGame} />
        {center === "team" ? <TeamProjectionsPanel game={game} day={day} onSelect={setGameId} />
          : <PlayersPanel game={game} rows={rows} minGames={data?.min_games} day={day} games={games} league={league} valueCounts={valueCounts} onValueChange={onValueChange}
              check={check} onCheck={(playerId, stat) => setCheck((c) => (c && c.playerId === playerId && c.stat === stat ? null : { playerId, stat }))} />}
        {nba ? <GameCenter game={game} rows={rows} playerPicks={playerPicks} teamPicks={teamPicks} onOpenPlayer={openPlayer} onOpenTeamGame={openTeamGame}
              check={check} onCloseCheck={() => setCheck(null)} onSelectCheck={(playerId, stat) => setCheck({ playerId, stat })} />
          : <EuroGameCenter game={game} comp={league} odds={game ? euroOdds[game.id] : null} onOpenPlayer={openPlayer} />}
      </div>
    </div>
  );
}
